export const LANE_PAGE_SIZE = 20;

export const WINDOW_MAX = LANE_PAGE_SIZE * 2;

export const LANE_FIELD_MAP: Record<number, string> = {
  0: 'StatusId',
  1: 'CategoryId',
  2: 'PriorityId',
  3: 'SupportGroupId',
  4: 'TeamId',
};

export const LANE_PREFIX_MAP: Record<number, string> = {
  0: 'status',
  1: 'category',
  2: 'priority',
  3: 'group',
  4: 'team',
};

export const buildLaneFilter = (laneKey: string, boardFilter: number): string => {
  if (laneKey === 'no_category') return 'CategoryId eq null';
  // A cleared support group can persist as either null or 0, so match both here.
  if (laneKey === 'no_group') return '(SupportGroupId eq null or SupportGroupId eq 0)';
  if (laneKey === 'no_team') return 'TeamId eq null';
  const prefix = LANE_PREFIX_MAP[boardFilter] ?? 'status';
  const id = laneKey.replace(`${prefix}_`, '');
  return `${LANE_FIELD_MAP[boardFilter] ?? 'StatusId'} eq ${id}`;
};

// The lane owns the grouping field; the view predicate (from ListFunctions.getFilterExpressions,
// the same one the list sends) carries everything else. AND them so a lane's window pages the
// matching set on the server instead of the newest 20 rows regardless of the filter.
export const combineLaneFilter = (laneFilter: string, viewPredicate?: string): string =>
  viewPredicate ? `(${laneFilter}) and (${viewPredicate})` : laneFilter;

// The custom-property predicate rides in its own `cpfilter` query param, not folded into
// `$filter` — the same split ListFunctions.getFilterQuery makes for the list. Empty string when
// the active view has no custom-property filter.
export const cpFilterParam = (cpFilter?: string): string =>
  cpFilter ? `&cpfilter=${cpFilter}` : '';

// A stable string over a custom view's *selected* filter values. The board refetches its lanes
// when this changes: a view's FiltersDropdownValues is rebuilt with real selections a beat after
// mount without its Id moving, so the Id alone can't drive the refetch. Kept pure and called on
// every render rather than memoised on the view object — that object's values are mutated in
// place (setIdOfRequestersFromPlatformUsers), which a reference-keyed memo would miss.
export const selectedViewFilterSignature = (
  filtersDropdownValues?: Array<{ id?: string | number; stringValue?: string; values?: any[] }>
): string =>
  (filtersDropdownValues ?? [])
    .map((f) => {
      const selected = (f.values ?? [])
        .filter((v: any) => v.selected)
        .map((v: any) => `${v.key}${v.date ?? ''}${v.number ?? ''}`)
        .join(',');
      return `${f.id}=${selected}${f.stringValue ?? ''}`;
    })
    .join('|');

export interface LanePage {
  hasMore: boolean;
  hasPrevious: boolean;
  isLoading: boolean;
  isLoadingTop: boolean;
  windowStart: number;
  skip: number;
}

export const lookaheadHasMore = (rawAllLen: number, topCount: number): boolean =>
  rawAllLen > topCount;

export const refreshWindowParams = (
  prev: LanePage | undefined,
  silent: boolean
): { windowStart: number; topCount: number } => {
  const windowStart = silent ? (prev?.windowStart ?? 0) : 0;
  const windowEnd = prev?.skip ?? LANE_PAGE_SIZE;
  const topCount = silent ? Math.max(LANE_PAGE_SIZE, windowEnd - windowStart) : LANE_PAGE_SIZE;
  return { windowStart, topCount };
};

export const shouldResetToTop = (
  silent: boolean,
  windowStart: number,
  rawAllLen: number
): boolean => silent && windowStart > 0 && rawAllLen === 0;

export const refreshLanePage = (
  windowStart: number,
  rawLen: number,
  hasMore: boolean
): LanePage => ({
  hasMore,
  hasPrevious: windowStart > 0,
  isLoading: false,
  isLoadingTop: false,
  windowStart,
  skip: windowStart + rawLen,
});

export const advanceWindowDown = (
  prev: LanePage,
  rawLen: number,
  rawAllLen: number
): { dropCount: number; page: LanePage } => {
  return {
    dropCount: 0,
    page: {
      ...prev,
      hasMore: lookaheadHasMore(rawAllLen, LANE_PAGE_SIZE),
      isLoading: false,
      skip: prev.skip + rawLen,
    },
  };
};

export const upRequestParams = (prev: LanePage): { fetchCount: number; skipStart: number } => {
  const fetchCount = Math.min(LANE_PAGE_SIZE, prev.windowStart);
  return { fetchCount, skipStart: prev.windowStart - fetchCount };
};

export const advanceWindowUp = (
  prev: LanePage,
  rawLen: number
): { skipStart: number; dropBottom: boolean; page: LanePage } => {
  const { skipStart } = upRequestParams(prev);
  const inMemoryAfter = (prev.skip - prev.windowStart) + rawLen;
  const dropBottom = inMemoryAfter > WINDOW_MAX;
  return {
    skipStart,
    dropBottom,
    page: {
      ...prev,
      isLoadingTop: false,
      windowStart: skipStart,
      hasPrevious: skipStart > 0,
      skip: dropBottom ? prev.skip - LANE_PAGE_SIZE : prev.skip,
      hasMore: dropBottom ? true : prev.hasMore,
    },
  };
};

export const dedupeById = <T extends { Id?: number }>(
  existing: ReadonlyArray<{ Id?: number }>,
  incoming: T[]
): T[] => {
  const existingIds = new Set(existing.map((t) => t.Id));
  return incoming.filter((t) => !existingIds.has(t.Id));
};
